import { GLSL_ARG, isMesh } from "../../typeMapping";
import { KERNELS } from "../../utilities/texture";
export default [
    {
        name: "st",
        type: "src",
        glsl: `return vec4(${GLSL_ARG.st}, 1.);`,
    },
    {
        name: "textureCube",
        type: "src",
        inputs: [
            {
                type: "samplerCube",
                name: GLSL_ARG.tex,
                default: NaN,
            },
        ],
        glsl: `
			vec3 st = ${GLSL_ARG.st};

			#ifdef ${isMesh}
			st -= 0.5;
			#endif

			return texture(${GLSL_ARG.tex}, normalize(st));
		`,
    },
    {
        name: "sobel",
        type: "src",
        inputs: [
            {
                type: "sampler2D",
                name: GLSL_ARG.tex,
                default: NaN,
            },
            {
                type: "float",
                name: "spread",
                default: 2,
            },
            {
                type: "float",
                name: "gain",
                default: 0.5,
            },
            {
                type: "float",
                name: "slopeness",
                default: 1,
            },
            {
                type: "float",
                name: "octaveFactor",
                default: 1,
            },
        ],
        glsl: `
			vec3 sob = vec3(0.0);
			vec2 derivative = vec2(0.0);
			
			float amplitude = gain;
			vec2 p = ${GLSL_ARG.st}.xy;
			for (int i = 0; i < 20; i++)
			{
				vec3 color = _tex(${GLSL_ARG.tex}, p).rgb;
				vec2 grad = _sobel(${GLSL_ARG.tex}, p, spread);
				derivative += grad;
				sob += amplitude * color / (1.0 + mix(0.0, dot(derivative, derivative), slopeness));
				amplitude = pow(amplitude * gain, octaveFactor);
			}

			return vec4(sob, _tex(${GLSL_ARG.tex}, ${GLSL_ARG.st}.xy).a);
        `,
        require: ["luminance", "sobel"],
    },
    {
        name: "convolution",
        type: "src",
        inputs: [
            {
                type: "sampler2D",
                name: GLSL_ARG.tex,
                default: NaN,
            },
            {
                type: "float[9]",
                name: "kernel",
                default: KERNELS.NORMAL,
            },
            {
                type: "float",
                name: "spread",
                default: 1,
            },
        ],
        glsl: `
			vec2 _spread = (vec2(1.0, 1.0) / resolution) * spread;
			return _convolution(${GLSL_ARG.tex}, ${GLSL_ARG.st}.xy, kernel, _spread);
		`,
        require: ["convolution"],
        transform: (input, kernel = "NORMAL", spread = 1) => {
            if (typeof kernel === "string") {
                kernel = kernel.toUpperCase();
                if (!(kernel in KERNELS)) {
                    console.warn(`kernel ${kernel} not found, using NORMAL instead`);
                    kernel = "NORMAL";
                }
                kernel = KERNELS[kernel];
            }
            if (Array.isArray(kernel) && kernel.length !== 9)
                kernel = new Array(9).fill(0).map((_, i) => kernel[i % kernel.length] || 0);
            return [input, kernel, spread];
        },
        help: `
			usage: convolution(<out or source>, <kernel_name> , <spread>)
			default: convolution(NaN, 'normal', 1)
			example: convolution(o0, 'normal', 1)

			kernels: ${Object.keys(KERNELS)
            .map(k => k.toLowerCase())
            .join(", ")} 
		`,
    },
    {
        name: "ascii",
        type: "src",
        inputs: [
            { name: "source", default: NaN, type: "sampler2D" },
            { name: "asciiTexture", default: NaN, type: "sampler2D" },
            { name: "charLen", default: [1, 1], type: "vec2", description: "[row, cols] of asciiTexture" },
            { name: "pixelateX", default: 20, type: "float" },
            { name: "pixelateY", default: 20, type: "float" },
            { name: "multiplier", default: 1, type: "float" },
        ],
        glsl: `
			vec2 st = ${GLSL_ARG.st}.xy;
			float rows = charLen.x;
			float cols = charLen.y;
			float chars = (rows * cols);
			vec2 pixelate = vec2(pixelateX, pixelateY);
            vec2 xy = (floor(st * pixelate) + 0.5) / pixelate;
            vec4 color = _tex(source, xy);
		
           	float luminosity = pow(luminance(color.rgb), multiplier) * chars;
			float luminosityCols = floor(mod(luminosity, cols));
			float luminosityRows = floor(luminosity / cols);

            float asciiCharOffsetX = luminosityCols / cols;
			float asciiCharOffsetY = luminosityRows / rows;
            
            vec2 position = vec2(
                fract(st.x * pixelate.x) * (1. / cols) + asciiCharOffsetX,
				fract(st.y  * pixelate.y) * (1. / rows) + asciiCharOffsetY
            );

            vec3 charColor = _tex(asciiTexture, position).xyz;
			
            return vec4(charColor, color.a);
		`,
        require: ["luminance"],
        transform: (source, asciiTexture, charLen, pixelateX, pixelateY, multiplier) => {
            if (typeof charLen === "number")
                charLen = [1, charLen];
            return [source, asciiTexture, charLen, pixelateX, pixelateY, multiplier];
        },
    },
    {
        name: "bloom",
        type: "src",
        inputs: {
            source: NaN,
            intensity: 2,
            spread: 1,
            threshold: 0.5,
        },
        glsl: `
			vec2 st = ${GLSL_ARG.st}.xy;
			vec4 sum = vec4(0.0);

			float uv_x = st.x;
			float uv_y = st.y;

			float spread_x = spread / resolution.x;
			float spread_y = spread / resolution.y;

			for (int n = 0; n < 9; ++n) {
				uv_y = st.y + (spread_y * float(n - 4));
				vec4 h_sum = vec4(0.0);
				h_sum += _tex(source, vec2(uv_x - (4.0 * spread_x), uv_y));
				h_sum += _tex(source, vec2(uv_x - (3.0 * spread_x), uv_y));
				h_sum += _tex(source, vec2(uv_x - (2.0 * spread_x), uv_y));
				h_sum += _tex(source, vec2(uv_x - spread_x, uv_y));
				h_sum += _tex(source, vec2(uv_x, uv_y));
				h_sum += _tex(source, vec2(uv_x + spread_x, uv_y));
				h_sum += _tex(source, vec2(uv_x + (2.0 * spread_x), uv_y));
				h_sum += _tex(source, vec2(uv_x + (3.0 * spread_x), uv_y));
				h_sum += _tex(source, vec2(uv_x + (4.0 * spread_x), uv_y));
				sum += h_sum / 9.0;
			}
			
			vec4 color = _tex(source, st);

			if (luminance(color) < threshold) {
				return color + ((sum / 9.0) * intensity);
			}

			return color;
		`,
        require: ["luminance"],
    },
    {
        name: "grid",
        type: "src",
        inputs: {
            scale: [10, 10],
            threshold: [0.1, 0.1],
        },
        glsl: `
			vec2 st = ${GLSL_ARG.st}.xy + vec2(threshold / scale / 2.);
			vec2 _grid = fract(st * scale);
			vec2 _lines = step(threshold, _grid);
			float _line = min(_lines.x, _lines.y);
			return vec4(vec3(_line), 1.0);

		`,
        transform: (scale, threshold) => {
            if (typeof scale === "number")
                scale = [scale, scale];
            if (typeof threshold === "number")
                threshold = [threshold, threshold];
            return [scale, threshold];
        },
    },
    {
        name: "opticalFlow",
        type: "src",
        inputs: {
            currentTex: NaN,
            prevTex: NaN,
            offset: 0.001,
            lambda: 0.1,
            speed: 0.2,
        },
        glsl: `
			vec2 uv = ${GLSL_ARG.st}.xy;
			vec3 n = _tex(currentTex, uv).rgb;
			vec3 p = _tex(prevTex, uv).rgb;

			vec3 n_x1 = _tex(currentTex, uv + vec2(offset, 0.0)).rgb;
			vec3 n_x2 = _tex(currentTex, uv - vec2(offset, 0.0)).rgb;
			vec3 n_y1 = _tex(currentTex, uv + vec2(0.0, offset)).rgb;
			vec3 n_y2 = _tex(currentTex, uv - vec2(0.0, offset)).rgb;

			vec3 p_x1 = _tex(prevTex, uv + vec2(offset, 0.0)).rgb;
			vec3 p_x2 = _tex(prevTex, uv - vec2(offset, 0.0)).rgb;
			vec3 p_y1 = _tex(prevTex, uv + vec2(0.0, offset)).rgb;
			vec3 p_y2 = _tex(prevTex, uv - vec2(0.0, offset)).rgb;

			vec3 Ix = (n_x1 - n_x2 + p_x1 - p_x2);
			vec3 Iy = (n_y1 - n_y2 + p_y1 - p_y2);
			vec3 It = (n - p) * 4.0;

			float ix = dot(Ix, vec3(0.2126, 0.7152, 0.0722));
			float iy = dot(Iy, vec3(0.2126, 0.7152, 0.0722));
			float it = dot(It, vec3(0.2126, 0.7152, 0.0722));

			float denom = dot(vec2(ix, iy), vec2(ix, iy)) + lambda;
			vec2 flow = -it * vec2(ix, iy) / denom;
			return vec4(flow, 0.0, 1.0);
		`,
        help: `
			usage: opticalFlow(<currentTex>, <prevTex>, <offset>, <lambda>, <speed>)
			default: opticalFlow(NaN, NaN, 0.001, 0.1, 0.2)
			example: opticalFlow(o0, o1, 0.001, 0.1, 0.2)
			example:
				await s0.initCam()
				src(s0).out(o0)

				opticalFlow(o0, o1).out(o2)
				src(o0).out(o1)
				render(o2)
		`,
    },
    {
        name: "dither",
        type: "src",
        inputs: [
            {
                type: "sampler2D",
                name: GLSL_ARG.tex,
                default: NaN,
            },
            {
                type: "sampler2D",
                name: "bayerTexture",
                default: NaN,
            },
            {
                type: "sampler2D",
                name: "paletteLUT",
                default: NaN,
            },
            {
                type: "float",
                name: "blendFactor",
                default: 0,
            },
            {
                type: "float",
                name: "ditherScale",
                default: 4,
            },
            {
                type: "float",
                name: "bayerScale",
                default: 4,
            },
        ],
        glsl: `
			vec2 st = ${GLSL_ARG.st}.xy;
            vec3 original_color = _tex(${GLSL_ARG.tex}, st).rgb;
        
            ivec2 bayerSize = textureSize(bayerTexture, 0);
            vec2 coord = floor(gl_FragCoord.xy / bayerScale);
            vec2 bayer_uv = mod(coord, vec2(bayerSize)) / vec2(bayerSize);
            float dither_offset = _tex(bayerTexture, bayer_uv).r - 0.5;
            int paletteSize = textureSize(paletteLUT, 0).x;
            vec3 color_to_quantize = original_color + dither_offset / float(paletteSize) * ditherScale;
            vec3 dithered_color = closestColor(color_to_quantize, paletteLUT);
            vec3 final_color = mix(color_to_quantize, dithered_color, blendFactor);
            return vec4(final_color, 1.0);
		`,
        transform: (tex, bayerTexture, paletteLUT, blendFactor, ditherScale, bayerScale) => {
            if (typeof bayerTexture === "undefined" || typeof paletteLUT === "undefined") {
                throw new Error("bayerTexture and paletteLUT are required for dither function");
            }
            console.log({ bayerTexture, paletteLUT });
            return [tex, bayerTexture, paletteLUT, blendFactor, ditherScale, bayerScale];
        },
        require: ["closestColor"],
        help: `
			usage: dither(<source>, <bayerTexture>, <paletteLUT>, <blendFactor>, <ditherScale>, <bayerScale>)
			default: dither(NaN, NaN, NaN, 0, 4, 4)
			example: dither(o0, bayerTex, paletteTex, 0.5, 4, 4)
			example:
				await s0.initCam()
				src(s0).out()

				b = bayerTexture()
				p = texture({ width: 10, height: 1, filter: 'nearest' })
				noise(2, 0, 0).draw(p)

				dither(o0, b, p, 0, 2, 10).out(o1)
				render(o1)
		`,
    },
];
